import { useMemo, useState, createElement, memo, Fragment } from 'react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Tabs, Tab, LoaderOverlay } from '@pocketlaw/tetris';
import rehype2react from 'rehype-react';
import rehypeStringify from 'rehype-stringify';
import { unified } from 'unified';
import { Div, Root, Section, Span, useBranchValues, usePreviewWorker } from 'app/domains/templates';
import { useCompileHTMLTemplate } from 'app/domains/templates/features/useCompileHTMLTemplate';
import { useMobile } from 'shared/domains/common-ui';
import { TemplatePreview } from 'shared/domains/documents';
import { highlight, generateVariableValues, useDeepFieldRefs } from './utils';
import { useDraftPreview } from '../../../DraftPreviewProvider';
import { getHtmlFromMarkdown } from '../../../utils';
import { TemplateData } from '../../TemplateData';
import { MetadataEntry } from '../../TemplateData/MetadataEntry';
import { HTMLPreviewContainer, StyledTabContent, StyledTabNav } from './styled';
const processToHTMLString = (node) => unified()
    .use(rehypeStringify)
    .stringify(unified()
    .use(highlight)
    .use(rehype2react, {
    Fragment: Root,
    createElement,
    components: {
        section: Section,
        div: Div,
        span: Span,
        h5: 'h4',
        h6: 'h4',
        'pl-highlight': 'pl-highlight',
    },
})
    .runSync(node));
function MarkdownTemplatePreview() {
    const { selectedTemplate, composer, loading } = useDraftPreview();
    const { tree } = composer;
    const { fields } = tree !== null && tree !== void 0 ? tree : {};
    const { latestVersion } = selectedTemplate || {};
    const { astBody: templateBody } = latestVersion || {};
    const keys = useDeepFieldRefs(fields);
    const variableValues = useMemo(() => generateVariableValues(keys), [keys]);
    const values = useBranchValues(tree, variableValues);
    const workerInput = useMemo(() => ({
        keys,
        values,
        astBody: templateBody,
    }), [keys, templateBody, values]);
    const worker = usePreviewWorker(workerInput);
    const markdownHTMLString = useMemo(() => (worker.data ? processToHTMLString(worker.data) : ''), [worker.data]);
    return (<LoaderOverlay enabled={loading}>
      {markdownHTMLString && <TemplatePreview html={markdownHTMLString}/>}
    </LoaderOverlay>);
}
function HTMLTemplatePreview() {
    const { selectedTemplate } = useDraftPreview();
    const { body } = selectedTemplate || {};
    const htmlWorker = useCompileHTMLTemplate(body || '', {});
    return (<HTMLPreviewContainer className="ck-content" 
    // The input HTML is sanitized in backend
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: htmlWorker.data || '' }}/>);
}
export const PreviewTabs = memo(() => {
    const { composer, loading, selectedTemplate } = useDraftPreview();
    const { descriptionBody } = composer;
    const { latestVersion } = selectedTemplate || {};
    const { isHtml } = latestVersion || {};
    const isMobile = useMobile();
    const [activeTab, setActiveTab] = useState('preview:description');
    const handleSelectTab = (tab) => {
        setActiveTab(tab);
    };
    return (<Tabs activeTab={activeTab} onSelect={handleSelectTab}>
      <StyledTabNav>
        <Tab id="preview:description">
          <Trans comment="Label for template description tab">Description</Trans>
        </Tab>
        <Tab id="preview:preview">
          <Trans comment="Label for template preview tab">Preview</Trans>
        </Tab>
      </StyledTabNav>
      <StyledTabContent tabId="preview:description">
        {isMobile && (<Fragment>
            <TemplateData />
            <MetadataEntry label={t({
                comment: 'Preview template - Description label (mobile only)',
                message: 'Description',
            })} component={null}/>
          </Fragment>)}
        <LoaderOverlay enabled={loading}>
          {descriptionBody && <TemplatePreview html={getHtmlFromMarkdown(descriptionBody)}/>}
        </LoaderOverlay>
      </StyledTabContent>
      <StyledTabContent tabId="preview:preview">
        <LoaderOverlay enabled={loading}>
          {isHtml ? <HTMLTemplatePreview /> : <MarkdownTemplatePreview />}
        </LoaderOverlay>
      </StyledTabContent>
    </Tabs>);
});
